import type React from "react";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import AddLinkIcon from "@mui/icons-material/AddLink";
import AddSharpIcon from "@mui/icons-material/AddSharp";
import AlignHorizontalCenterIcon from "@mui/icons-material/AlignHorizontalCenter";
import AlignHorizontalLeftIcon from "@mui/icons-material/AlignHorizontalLeft";
import AlignHorizontalRightIcon from "@mui/icons-material/AlignHorizontalRight";
import AlignVerticalBottomIcon from "@mui/icons-material/AlignVerticalBottom";
import AlignVerticalCenterIcon from "@mui/icons-material/AlignVerticalCenter";
import AlignVerticalTopIcon from "@mui/icons-material/AlignVerticalTop";
import AttachFileOutlinedIcon from "@mui/icons-material/AttachFileOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import CachedSharpIcon from "@mui/icons-material/CachedSharp";
import CallSplitOutlinedIcon from "@mui/icons-material/CallSplitOutlined";
import ClearIcon from "@mui/icons-material/Clear";
import CloseIcon from "@mui/icons-material/Close";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DeleteOutlinedIcon from "@mui/icons-material/DeleteOutlined";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import ArrowUpwardSharpIcon from "@mui/icons-material/ArrowUpwardSharp";
import CloseSharpIcon from "@mui/icons-material/CloseSharp";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import FileUploadOutlinedIcon from "@mui/icons-material/FileUploadOutlined";
import FilterListIcon from "@mui/icons-material/FilterList";
import FilterAltOutlinedIcon from "@mui/icons-material/FilterAltOutlined";
import FilterAltOffOutlinedIcon from "@mui/icons-material/FilterAltOffOutlined";
import FindReplaceOutlinedIcon from "@mui/icons-material/FindReplaceOutlined";
import GridOnOutlinedIcon from "@mui/icons-material/GridOnOutlined";
import HistoryIcon from "@mui/icons-material/History";
import HubOutlinedIcon from "@mui/icons-material/HubOutlined";
import RestartAltOutlinedIcon from "@mui/icons-material/RestartAltOutlined";
import InsertLinkIcon from "@mui/icons-material/InsertLink";
import KeyOutlinedIcon from "@mui/icons-material/KeyOutlined";
import ListIcon from "@mui/icons-material/List";
import LayersOutlinedIcon from "@mui/icons-material/LayersOutlined";
import LockOpenOutlinedIcon from "@mui/icons-material/LockOpenOutlined";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import PauseOutlinedIcon from "@mui/icons-material/PauseOutlined";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import NotesOutlinedIcon from "@mui/icons-material/NotesOutlined";
import NumbersOutlinedIcon from "@mui/icons-material/NumbersOutlined";
import RocketLaunchIcon from "@mui/icons-material/RocketLaunch";
import RocketLaunchOutlinedIcon from "@mui/icons-material/RocketLaunchOutlined";
import SendOutlinedIcon from "@mui/icons-material/SendOutlined";
import ShareOutlinedIcon from "@mui/icons-material/ShareOutlined";
import StorageOutlinedIcon from "@mui/icons-material/StorageOutlined";
import StraightenIcon from "@mui/icons-material/Straighten";
import TuneIcon from "@mui/icons-material/Tune";
import ViewQuiltOutlinedIcon from "@mui/icons-material/ViewQuiltOutlined";
import MycorrLogo from "@/assets/images/MycorrMedium.svg?react";
import { AddColumnLeftIcon, AddColumnRightIcon } from "./AddColumnIcon";
import { CastIcon } from "./CastIcon";
import { GraphModeIcon } from "./GraphModeIcon";
import {
  AddToInvestigationIcon,
  ClearInvestigationIcon,
  InvestigateRowsIcon,
} from "./InvestigateIcon";
import {
  DistributeHorizontallyIcon,
  DistributeVerticallyIcon,
} from "./DistributeIcon";

type ActionIcon = React.ComponentType<{
  className?: string;
  style?: React.CSSProperties;
}>;

export interface CatalogEntry {
  Icon: ActionIcon;
  /** Its name in every menu, with no ellipsis. */
  label: string;
  countLabel?: (count: number) => string;
}

/** The model, dataset and space menus keep their sentence-case spelling. */
export const MANAGE_ACCESS_MENU_LABEL = "Manage access";

/** One icon and label per action; change an icon here, never at a call site. */
export const CATALOG = {
  tableGraph: { Icon: GraphModeIcon, label: "Switch to graph view" },
  backToTable: { Icon: GridOnOutlinedIcon, label: "Back to table canvas" },
  rowGraph: {
    Icon: InvestigateRowsIcon,
    label: "Investigate rows",
    countLabel: (n: number) => `Investigate ${n} ${n === 1 ? "row" : "rows"}`,
  },
  clearInvestigation: {
    Icon: ClearInvestigationIcon,
    label: "Clear investigation",
  },
  resetGraph: { Icon: RestartAltOutlinedIcon, label: "Reset graph" },
  openRowGraph: {
    Icon: InvestigateRowsIcon,
    label: "Open rows investigation",
  },
  rowGraphAdd: {
    Icon: AddToInvestigationIcon,
    label: "Add rows to investigation",
    countLabel: (n: number) =>
      `Add ${n} ${n === 1 ? "row" : "rows"} to investigation`,
  },

  version: { Icon: HistoryIcon, label: "Checkpoints" },
  // Commit mode's section of the bar, drawn as the canvas drew it: the pause at rest, ✕ beside ↑ while open.
  startCommit: {
    Icon: PauseOutlinedIcon,
    label: "Start commit mode",
  },
  commit: { Icon: ArrowUpwardSharpIcon, label: "Commit staged edits" },
  discardEdits: { Icon: CloseSharpIcon, label: "Discard staged edits" },
  addFilter: { Icon: FilterAltOutlinedIcon, label: "Filter Table View" },
  editFilter: { Icon: FilterAltOutlinedIcon, label: "Edit filter" },
  removeFilter: { Icon: FilterAltOffOutlinedIcon, label: "Remove filter" },
  filterByColumn: {
    Icon: FilterAltOutlinedIcon,
    label: "Filter by this column",
  },
  filterByValue: { Icon: FilterAltOutlinedIcon, label: "Filter by this value" },
  leaveCommit: { Icon: CloseSharpIcon, label: "Leave commit mode" },
  // Commit's answer when it would retype a column: keep the types, drop those values.
  checkpoints: { Icon: HistoryIcon, label: "Checkpoints" },
  // An importer's history: the source's releases, which it may restore.
  releases: { Icon: HistoryIcon, label: "Releases" },
  setRelease: { Icon: RocketLaunchOutlinedIcon, label: "Set as release" },
  clearRelease: { Icon: RocketLaunchIcon, label: "Unrelease" },
  syncRelease: { Icon: CachedSharpIcon, label: "Sync to latest release" },

  newTable: { Icon: AddSharpIcon, label: "New Table" },
  // The Directory's icon for a table (`ItemInstanceIcon`), on the row that makes one.
  importTable: { Icon: FileUploadOutlinedIcon, label: "Import" },
  importFile: { Icon: FileUploadOutlinedIcon, label: "Upload File" },
  importConnection: { Icon: StorageOutlinedIcon, label: "Connections" },
  importMycorr: { Icon: MycorrLogo, label: "Mycorr" },
  attachmentColumn: {
    Icon: AttachFileOutlinedIcon,
    label: "Add attachment column",
  },
  viewFile: { Icon: VisibilityOutlinedIcon, label: "View file" },
  addColumnRight: { Icon: AddColumnRightIcon, label: "Add column right" },
  addColumnLeft: { Icon: AddColumnLeftIcon, label: "Add column left" },
  arrange: { Icon: ViewQuiltOutlinedIcon, label: "Arrange" },
  alignLeft: { Icon: AlignHorizontalLeftIcon, label: "Align left" },
  alignRight: { Icon: AlignHorizontalRightIcon, label: "Align right" },
  alignHorizontalCenters: {
    Icon: AlignHorizontalCenterIcon,
    label: "Align horizontal centers",
  },
  alignTop: { Icon: AlignVerticalTopIcon, label: "Align top" },
  alignBottom: { Icon: AlignVerticalBottomIcon, label: "Align bottom" },
  alignVerticalCenters: {
    Icon: AlignVerticalCenterIcon,
    label: "Align vertical centers",
  },
  distributeHorizontally: {
    Icon: DistributeHorizontallyIcon,
    label: "Distribute horizontally",
  },
  distributeVertically: {
    Icon: DistributeVerticallyIcon,
    label: "Distribute vertically",
  },

  export: { Icon: DownloadOutlinedIcon, label: "Export" },
  exportCsv: { Icon: DownloadOutlinedIcon, label: "CSV" },
  exportParquet: { Icon: DownloadOutlinedIcon, label: "Parquet" },
  exportSheets: { Icon: GridOnOutlinedIcon, label: "Google Sheets" },

  // A table's, as the details ⋮ spells it; the model menu writes "access".
  manageAccess: { Icon: ShareOutlinedIcon, label: "Manage Access" },
  addToDataset: { Icon: LayersOutlinedIcon, label: "Add to dataset" },
  // The same dialog, named for what it can do once a table is in a dataset.
  addOrRemoveDataset: {
    Icon: LayersOutlinedIcon,
    label: "Add to or remove from dataset",
  },
  transfer: { Icon: SendOutlinedIcon, label: "Transfer Ownership" },
  copyTableId: { Icon: ContentCopyIcon, label: "Copy table ID" },
  copyModelId: { Icon: ContentCopyIcon, label: "Copy model ID" },

  setDataType: { Icon: TuneIcon, label: "Set Data Type" },
  displayTime: { Icon: AccessTimeIcon, label: "Display" },
  displayNumber: { Icon: NumbersOutlinedIcon, label: "Display Format" },
  displayText: { Icon: TuneIcon, label: "Display Format" },
  // From a cell: the setting is the whole column's.
  displayTimeForColumn: { Icon: AccessTimeIcon, label: "Display for column" },
  displayNumberForColumn: {
    Icon: NumbersOutlinedIcon,
    label: "Display Format for column",
  },
  displayTextForColumn: { Icon: TuneIcon, label: "Display Format for column" },
  castColumn: { Icon: CastIcon, label: "Cast Column" },
  manageCategories: { Icon: ListIcon, label: "Manage categories" },
  setUnit: { Icon: StraightenIcon, label: "Set unit" },
  setUnitForColumn: { Icon: StraightenIcon, label: "Set unit for column" },
  clearUnit: { Icon: CloseIcon, label: "Clear assignment" },
  // The column list's toggle shows the state it would leave: an open lock
  // locks.
  lockType: { Icon: LockOpenOutlinedIcon, label: "Lock data type" },
  unlockType: { Icon: LockOutlinedIcon, label: "Unlock data type" },
  addDescription: { Icon: NotesOutlinedIcon, label: "Add description" },
  editDescription: { Icon: EditOutlinedIcon, label: "Edit description" },
  removeDescription: { Icon: ClearIcon, label: "Remove description" },
  editIntents: { Icon: KeyOutlinedIcon, label: "Edit intents" },
  splitColumn: { Icon: CallSplitOutlinedIcon, label: "Split column values" },
  mapToReference: { Icon: HubOutlinedIcon, label: "Map to reference" },
  extractUnique: { Icon: FilterListIcon, label: "Extract Unique Values" },
  searchReplace: { Icon: FindReplaceOutlinedIcon, label: "Search & replace" },
  linkColumn: { Icon: AddLinkIcon, label: "Link Column" },
  removeLink: { Icon: DeleteOutlinedIcon, label: "Delete table link" },
  // The Links section header's chain: its "+" button reads as Add on the bar.
  tableLinks: { Icon: InsertLinkIcon, label: "Add table link" },
  deleteFromTable: { Icon: DeleteOutlinedIcon, label: "Delete from Table" },
  deleteSelected: { Icon: DeleteOutlinedIcon, label: "Delete selected" },

  more: { Icon: MoreVertIcon, label: "More actions" },
} as const satisfies Record<string, CatalogEntry>;

export type CatalogKey = keyof typeof CATALOG;

export type DisplayTarget = "display" | "format" | "stringFormat";

export const DISPLAY_ENTRY: Record<DisplayTarget, CatalogKey> = {
  display: "displayTime",
  format: "displayNumber",
  stringFormat: "displayText",
};

export const DISPLAY_ENTRY_FOR_COLUMN: Record<DisplayTarget, CatalogKey> = {
  display: "displayTimeForColumn",
  format: "displayNumberForColumn",
  stringFormat: "displayTextForColumn",
};
